import * as React from "react";
import { useState, useEffect } from "react";
import { Box, IconButton, InputAdornment, TextField, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Modal, Typography } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import EmailIcon from '@mui/icons-material/Email';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from '@mui/icons-material/Delete';
import ODMTable from "../components/ODMTable";
import { THeadUsers } from "../interfaces/Utente";
import { ModalAlert, ModalData, modalDataClean, MODALTYPE } from "../components/ModalAlert";
import { ReactComponent as AlertMessageDisactivate } from '../assets/icons/alertMessagedisactivate.svg';
import { ReactComponent as AlertMessageInfo } from '../assets/icons/alertMessageinfo.svg';
import alert from '../assets/icons/alert.svg';
import check from '../assets/icons/check.svg';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ListaUtenti: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [modalData, setModalData] = useState<ModalData>(modalDataClean);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<{
    icon: React.ReactNode | null;
    message: string;
  }>({ icon: null, message: "" });

  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const specialCharacterControlRegex = /^(?=.*[^A-Za-z0-9]).*$/;
  const numberControlRegex = /^(?=.*\d).*$/;
  const lowercaseControlRegex = /^(?=.*[a-z]).*$/;
  const uppercaseControlRegex = /^(?=.*[A-Z]).*$/;
  const characterNumberControlRegex = /^.{8,}$/;
  const checkPassword = (password: string): boolean => {
    return !(
      specialCharacterControlRegex.test(password) &&
      numberControlRegex.test(password) &&
      lowercaseControlRegex.test(password) &&
      uppercaseControlRegex.test(password) &&
      characterNumberControlRegex.test(password)
    );
  };
  const [showPassword, setShowPassword] = useState(false);

  const isValidEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const isFormValid =
    newUserData.name.trim() !== "" &&
    newUserData.email.trim() !== "" &&
    isValidEmail(newUserData.email) &&
    newUserData.password.trim() !== ""

  const getAuthenticationIcon = (authMethod: string) => {
    switch (authMethod?.toLowerCase()) {
      case "email":
        return <EmailIcon color="primary" />;
      case "google":
        return <GoogleIcon style={{ color: "#DB4437" }} />;
      case "apple":
        return <AppleIcon style={{ color: "black" }} />;
      default:
        return <EmailIcon color="primary" />;
    }
  };

  const fetchUsers = async () => {
    try {
      const headers = new Headers();
      headers.set("Accept", "application/json");
      const response = await fetch("/api/v0/users", {
        method: "GET",
        credentials: "include",
        headers,
      });
      if (!response.ok) throw new Error("Errore nel recupero degli utenti");
      const data = await response.json();
      setUsers(data);
      setFilteredUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const eliminaUtente = async (email: string) => {
    setLoading(true);
    try {
      const headers = new Headers();
      headers.set("Accept", "application/json");

      const response = await fetch(`/api/v0/user/${email}`, {
        method: "DELETE",
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || "Errore durante l'eliminazione dell'utente");
      }

      setUsers((prev) => prev.filter((u) => u.email !== email));
      setFilteredUsers((prev) => prev.filter((u) => u.email !== email));
      setModalData(modalDataClean);

      setSnackbarMessage({
        icon: <AlertMessageDisactivate />,
        message: "Utente eliminato con successo",
      });
      setSnackbarOpen(true);
    } catch (error: any) {
      // console.error("Errore durante la chiamata DELETE:", error);
      setSnackbarMessage({
        icon: <AlertMessageDisactivate />,
        message: error.message || "Errore durante l'eliminazione dell'utente",
      });
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const mappedUsers = React.useMemo(() => {
    return filteredUsers.map((user) => ({
      ...user,
      start_date: user.start_date
        ? format(new Date(user.start_date), "dd/MM/yyyy")
        : "Data non disponibile",
      autenticationIcon: getAuthenticationIcon(user.authentication || ""),
      actions: (
        <IconButton
          color="error"
          onClick={() => {
            setSelectedUser(user);
            setModalData({
              show: true,
              title: `Elimina utente ${user.email}`,
              message: `Questa azione è irreversibile. Vuoi procedere?`,
              type: MODALTYPE.DELETE,
              action: async () => {
                await eliminaUtente(user.email);
              },
            });
          }}
        >
          <DeleteIcon />
        </IconButton>
      ),
    }));
  }, [filteredUsers]);

  useEffect(() => {
    const filtered = users.filter(
      (user) =>
        // user.start_date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const closeModal = () => setModalData({ ...modalData, show: false });

  const handleOpenCreateUser = () => setOpenCreateUser(true);
  const handleCloseCreateUser = () => {
    setOpenCreateUser(false);
    setNewUserData({ name: "", email: "", password: "" });
  };

  const handleCreateUser = async () => {
    try {
      const { ...userToSend } = newUserData;

      setSubmitting(true)
      const response = await fetch("/api/v0/user/new", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(userToSend),
      });
      setSubmitting(false)

      if (response.status === 409) {
        setSnackbarMessage({
          icon: <AlertMessageDisactivate />,
          message: "Mail già registrata",
        });
        setSnackbarOpen(true);
        return;
      }

      if (!response.ok){
        //throw new Error("Errore nella creazione utente");
        const data = await response.json();
        const text = data.details;
        // throw new Error(text || "Errore nella creazione utente");
        setSnackbarMessage({
          icon: <AlertMessageDisactivate />,
          message: text || "Errore nella creazione utente",
        });
        setSnackbarOpen(true);
      } else {
        const responseObj: {user: Object, details: string} = await response.json();
        const createdUser = responseObj.user
        setUsers((prev) => [...prev, createdUser]);
        setFilteredUsers((prev) => [...prev, createdUser]);

        setSnackbarMessage({
          icon: <AlertMessageInfo />,
          message: responseObj.details != "" ? responseObj.details : "Utente creato con successo",
        });
        setSnackbarOpen(true);

        handleCloseCreateUser();
      }
    } catch (error: any) {
      // console.error(error.message);
      setSnackbarMessage({
        icon: <AlertMessageDisactivate />,
        message:
          error.message || "Si è verificato un errore durante la creazione",
      });
      setSnackbarOpen(true);
    }
  };

  const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
  };

  return (
    <Box sx={{ display: "flex", height: "100%", width: "100%" }}>
      <Box
        component="main"
        sx={{ flexGrow: 1, height: "100%", width: "100%", overflow: "hidden" }}
      >
        <div className="p-3 backgroundPage" style={{ height: "100%", width: "100%" }}>
          <h1>Lista utenti in app</h1>
          <p>Monitora gli utenti che effettuano la registrazione in app</p>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <TextField
              variant="outlined"
              label="Cerca utente"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <button
              className="btn btn-light me-5"
              style={{ whiteSpace: "nowrap" }}
              onClick={handleOpenCreateUser}
            >
              + Nuovo utente
            </button>

            {/* ✅ Modal creazione utente */}
            <Modal open={openCreateUser} onClose={handleCloseCreateUser}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  borderRadius: 3,
                  p: 2,
                  width: 360,
                  outline: "none",
                }}
              >
                <div className="row">
                  <div className="col text-start">
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Nuovo utente
                    </Typography>
                  </div>
                  <div className="col text-end">
                    <button
                      type="button"
                      className="btn-close"
                      onClick={handleCloseCreateUser}
                    ></button>
                  </div>
                </div>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}
                >
                  <TextField
                    label="Nome*"
                    variant="outlined"
                    size="small"
                    value={newUserData.name}
                    onChange={(e) =>
                      setNewUserData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Email*"
                    variant="outlined"
                    size="small"
                    value={newUserData.email}
                    error={
                      !isValidEmail(newUserData.email)
                    }
                    onChange={(e) =>
                      setNewUserData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Password*"
                    variant="outlined"
                    size="small"
                    type={showPassword ? 'text' : 'password'}
                    value={newUserData.password}
                    onChange={(e) =>
                      setNewUserData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    fullWidth
                    error={
                      checkPassword(newUserData.password)
                    }
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={togglePasswordVisibility}>
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                  />
                  <div className="row">
                    <div className="flex flex-col gap-1 pl-5"><img src={specialCharacterControlRegex.test(newUserData.password) ? check : alert} alt="icon" width={20} />Caratteri speciali</div>
                    <div className="flex flex-col gap-1"><img src={numberControlRegex.test(newUserData.password) ? check : alert} alt="icon" width={20} />Numeri</div>
                    <div className="flex flex-col gap-1"><img src={lowercaseControlRegex.test(newUserData.password) ? check : alert} alt="icon" width={20} />Lettere minuscole</div>
                    <div className="flex flex-col gap-1"><img src={uppercaseControlRegex.test(newUserData.password) ? check : alert} alt="icon" width={20} />Lettere maiuscole</div>
                    <div className="flex flex-col gap-1"><img src={characterNumberControlRegex.test(newUserData.password) ? check : alert} alt="icon" width={20} />Lunghezza minima 8</div>
                  </div>
                </Box>

                <div className="row text-centre">
                  <div className="col d-grid gap-1">
                    <button
                      className="btn btn-light"
                      onClick={handleCloseCreateUser}
                    >
                      Annulla
                    </button>
                  </div>
                  <div className="col d-grid gap-1">
                    <button
                      className="btn btn-success"
                      onClick={handleCreateUser}
                      disabled={!isFormValid || submitting}
                    >
                      Crea
                    </button>
                  </div>
                </div>
              </Box>
            </Modal>
          </Box>

          <ODMTable
            columns={THeadUsers}
            rows={mappedUsers}
            navTable={""}
            edit={false}
            selectedRowId={1}
          />
        </div>
      </Box>

      <ModalAlert modalData={modalData} onClose={closeModal} />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          sx={{
            width: "100%",
            backgroundColor: "black",
            color: "white",
            borderRadius: 3,
          }}
          icon={snackbarMessage.icon}
        >
          {snackbarMessage.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListaUtenti;
